<template>
  <div class="container">
    <!-- Top Header Info Section Card -->
    <div class="col-sm-12 header-card-wrapper">
      <div class="card header-info-card p-3">
        <div class="dropdowns-wrapper">
          <div class="dropdown-container">
            <div class="dropdown-item">
              <label for="startDatePicker">Invoicing Date</label>
              <input type="date" v-model="currentDate" class="date-box" />
            </div>
          </div>

          <div class="dropdown-container">
            <div class="dropdown-item">
              <label for="distributor">Select Distributor</label>
              <select id="distributor" v-model="selectedDistributor" :disabled="isDistributorLocked || isBusy">
                <option v-for="d in distributorList" :key="d.profile_id ?? d.id" :value="String(d.profile_id ?? d.id)">
                  {{
                    d.business_name ||
                    d.business_display_name ||
                    d.name ||
                    "Distributor #" + (d.profile_id ?? d.id)
                  }}
                </option>
              </select>
            </div>
          </div>

          <div class="dropdown-container">
            <div class="dropdown-item">
              <label>Invoice Type</label>
              <div class="type-box-selector">
                <label class="type-box"
                  :class="{ active: invoiceType === 'customer', disabled: selectedItems.length > 0 }">
                  <input type="radio" value="customer" v-model="invoiceType" :disabled="selectedItems.length > 0"
                    class="d-none" />
                  <span>Customer</span>
                </label>
                <label class="type-box"
                  :class="{ active: invoiceType === 'dealer', disabled: selectedItems.length > 0 }">
                  <input type="radio" value="dealer" v-model="invoiceType" :disabled="selectedItems.length > 0"
                    class="d-none" />
                  <span>Dealer</span>
                </label>
              </div>
            </div>
          </div>

          <div class="dropdown-container" v-if="invoiceType === 'customer'">
            <div class="dropdown-item">
              <label for="customer-dealer-select">
                Customer Dealer
              </label>

              <select id="customer-dealer-select" v-model="selectedDealerId" class="form-control" :disabled="!cashSaleDealerList.length ||
                isBusy ||
                selectedItems.length > 0
                ">
                <option :value="null" disabled>
                  Select Customer Dealer
                </option>

                <option v-for="dealer in cashSaleDealerList" :key="dealer.profile_id ?? dealer.id"
                  :value="dealer.profile_id ?? dealer.id">
                  {{ getDealerLabel(dealer) || "Dealer #" + (dealer.profile_id ?? dealer.id) }}
                </option>
              </select>
            </div>
          </div>

          <div class="dropdown-container" v-else>
            <div class="dropdown-item">
              <label for="dealer-select">Select Dealer</label>
              <select id="dealer-select" v-model="selectedDealerId"
                :disabled="!regularDealerList.length || isBusy || selectedItems.length > 0">
                <option value="" disabled>Select Dealer</option>
                <option v-for="dealer in regularDealerList" :key="dealer.profile_id ?? dealer.id"
                  :value="dealer.profile_id ?? dealer.id">
                  {{ getDealerLabel(dealer) || "Dealer #" + (dealer.profile_id ?? dealer.id) }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Items Section Card (Combined Item Addition & Added Items Table) -->
    <div class="col-sm-12 item-card-wrapper">
      <div class="card item-selection-card p-3">
        <!-- Item Input Controls (Full Width) -->
        <div class="item-add-row">
          <!-- Item Name Dropdown (Expanded) -->
          <div class="item-field item-name-field">
            <label for="item-select">Item Name</label>
            <select id="item-select" v-model="selectedVariantId" class="form-control">
              <option value="" disabled selected>Select Item</option>
              <option v-for="item in items" :key="item.id" :value="item.id" :disabled="isItemSelected(item.id)">
                {{ item.variant_name }}
              </option>
            </select>
          </div>

          <!-- Item Quantity (Compact width) -->
          <div class="item-field item-qty-field">
            <label for="quantity-input">Item Quantity</label>
            <input type="number" id="quantity-input" v-model="itemQuantity" placeholder="Enter quantity" min="0"
              @keydown.enter="addItem" class="form-control" />
            <div class="mt-1" v-if="selectedVariantId">
              <small class="text-muted font-weight-bold">Available Stock: {{ selectedStock }}</small>
            </div>
            <div v-if="selectedVariantId && getDealerBalance(selectedVariantId) !== null">
              <small class="text-muted font-weight-bold">
                Dealer Limit Remaining: {{ getDealerBalance(selectedVariantId) }}
              </small>
            </div>
          </div>

          <!-- Add Button (Fixed normal size) -->
          <div class="item-field item-btn-field">
            <label class="d-none d-md-block invisible">Add</label>
            <button type="button" class="btn button-default btn-add-item" @click="addItem">
              Add
            </button>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-spinner mt-2">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </div>

        <!-- Added Items Table inside the same card -->
        <div class="table-responsive mt-2" v-if="selectedItems.length > 0">
          <table class="table mb-0">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Item Price</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in selectedItems" :key="index">
                <td>{{ getItemNameById(item.itemVariantId) }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ formatNumber(item.priceWithVat) }}</td>
                <td>{{ formatNumber(item.vatTotal) }}</td>
                <td>
                  <i class="fa fa-edit text-primary mr-2" style="cursor: pointer" @click="editItem(index)"
                    title="Edit Item"></i>
                  <i class="fa fa-trash text-danger" style="cursor: pointer" @click="confirmDeleteItem(index)"
                    title="Delete Item"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Dealer Financial Overview -->
    <div v-if="
      invoiceType === 'dealer' && selectedDealerId && selectedItems.length > 0
    " class="col-sm-12 dealer-financials-wrapper">
      <div class="card p-3 dealer-financials-card">
        <h5 class="mb-3">Dealer Financial Overview</h5>
        <span v-if="dealerFinancialsLoading">
          <i class="fa fa-spinner fa-spin"></i> Loading...
        </span>

        <template v-if="dealerFinancials && !dealerFinancialsLoading">
          <!-- Collapsible: Due Payments -->
          <div v-if="duePaymentsList.length > 0" class="mb-2">
            <button type="button"
              class="btn btn-sm btn-outline-primary w-100 text-left d-flex justify-content-between align-items-center"
              @click="showDuePayments = !showDuePayments">
              <span>
                <i class="fa-solid fa-clock-rotate-left mr-1"></i>
                Due Payments ({{ duePaymentsList.length }})
              </span>
              <i :class="showDuePayments
                ? 'fa-solid fa-chevron-up'
                : 'fa-solid fa-chevron-down'
                "></i>
            </button>
            <div v-if="showDuePayments" class="financials-list mt-2">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Date</th>
                    <th>Full Amount (Rs)</th>
                    <th>Due Amount (Rs)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in duePaymentsList" :key="'due-' + i">
                    <td>{{ p.invoice_no }}</td>
                    <td>{{ formatDateTime(p.date) }}</td>
                    <td>{{ formatNumber(p.full_amount) }}</td>
                    <td>{{ formatNumber(p.due_amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Collapsible: Advance Payments -->
          <div v-if="advancePaymentsList.length > 0">
            <button type="button"
              class="btn btn-sm btn-outline-primary w-100 text-left d-flex justify-content-between align-items-center"
              @click="showAdvancePayments = !showAdvancePayments">
              <span>
                <i class="fa-solid fa-money-bill-wave mr-1"></i>
                Advance Payments ({{ advancePaymentsList.length }})
              </span>
              <i :class="showAdvancePayments
                ? 'fa-solid fa-chevron-up'
                : 'fa-solid fa-chevron-down'
                "></i>
            </button>
            <div v-if="showAdvancePayments" class="financials-list mt-2">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Date</th>
                    <th>Amount (Rs)</th>
                    <th>Balance (Rs)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in advancePaymentsList" :key="'adv-' + i">
                    <td>{{ p.pay_method || "-" }}</td>
                    <td>{{ formatDateTime(p.paid_at) }}</td>
                    <td>{{ formatNumber(p.advance_amount) }}</td>
                    <td>{{ formatNumber(p.advance_balance) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p v-if="!duePaymentsList.length && !advancePaymentsList.length" class="text-muted mb-0">
            <small>No outstanding due or advance payments for this dealer.</small>
          </p>
        </template>
      </div>
    </div>

    <!-- Payment Summary Section -->
    <template v-if="selectedItems.length > 0">
      <div class="col-sm-12 payment-summary-wrapper">
        <div class="card payment-summary-card p-3">
          <h5>Payment Summary</h5>
          <div class="d-flex justify-content-between my-2">
            <span>Grand Total:</span>
            <span class="total-amount">{{ formatNumber(grandTotal) }}</span>
          </div>

          <!-- DISCOUNT SECTION — commented out
<div class="discount-input">
  <span>Discount:</span>

  <div class="d-flex align-items-center gap-2">
    <input type="number"
          v-model.number="discount"
          min="0"
          class="form-control"
          :placeholder="discountType === 'percentage' ? '%' : 'Rs'"
          style="width: 100px;" />

    <label class="ml-2">
      <input type="radio" value="value" v-model="discountType" /> Rs
    </label>

    <label class="ml-2">
      <input type="radio" value="percentage" v-model="discountType" /> %
    </label>
  </div>
</div>
-->

          <div class="d-flex justify-content-between my-2 font-weight-bold">
            <span>Final Total:</span>
            <span class="final-amount">{{ formatNumber(finalTotal) }}</span>
          </div>

          <div v-if="invoiceType === 'dealer'" class="due-amount">
            <span>Current Invoice Due:</span>
            <span>Rs {{ formatNumber(dueAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- Total Outstanding Due Section — dealer only -->
      <div v-if="invoiceType === 'dealer'" class="col-sm-12 outstanding-due-wrapper">
        <div class="card outstanding-due-card p-3">
          <h5>Total Outstanding Due</h5>

          <div class="d-flex justify-content-between my-2">
            <span>Previous Invoices Due:</span>
            <span>Rs {{ formatNumber(previousDue) }}</span>
          </div>

          <div class="d-flex justify-content-between my-2" style="color: #dc3545">
            <span>Current Invoice Due:</span>
            <span>Rs {{ formatNumber(dueAmount) }}</span>
          </div>

          <hr class="my-2" />

          <div class="due-amount">
            <span>TOTAL DUE:</span>
            <span>Rs {{ formatNumber(totalDueWithInvoice) }}</span>
          </div>
        </div>
      </div>

      <!-- Credit Limit Section — dealer only -->
      <div v-if="invoiceType === 'dealer'" class="col-sm-12 credit-limit-wrapper">
        <div class="card credit-limit-card p-3" :class="creditExceeded ? 'credit-card-danger' : 'credit-card-success'">
          <div class="d-flex justify-content-between my-2">
            <span class="font-weight-bold">Credit Limit:</span>
            <span class="font-weight-bold">Rs {{ formatNumber(creditLimit) }}</span>
          </div>

          <div class="d-flex justify-content-between my-2">
            <span class="font-weight-bold">Available Credit:</span>
            <span class="font-weight-bold" :style="creditExceeded ? { color: '#dc3545' } : { color: '#32cc59' }
              ">Rs {{ formatNumber(availableCredit) }}</span>
          </div>

          <!-- Error message when exceeded -->
          <div v-if="creditExceeded" class="mt-2 d-flex align-items-center gap-2" style="color: #dc3545">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <small>
              Credit limit exceeded by Rs {{ formatNumber(exceedingAmount) }}.
              Invoice cannot be saved.
            </small>
          </div>
        </div>
      </div>

      <!-- Payment Section -->
      <div class="col-sm-12 payment-section-wrapper">
        <div class="row">
          <!-- Left side - Payment Add Form -->
          <div class="col-md-6 mb-3 mb-md-0">
            <div class="card payment-form-card p-3 h-100">
              <h5 class="font-weight-bold mb-3">Invoice Payment</h5>

              <div class="payment-form-vertical">
                <!-- 1. Payment Method Dropdown -->
                <div class="form-group mb-3">
                  <label>Payment Method <span class="req">*</span></label>
                  <select v-model="tempPayment.payment_method" class="form-control">
                    <option disabled value="">-- Select Payment Method --</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option v-if="invoiceType === 'dealer'" value="Advance">Advance Payment</option>
                    <option value="Credit/Debit Card">Credit/Debit Card</option>
                    <option value="Mobile Payment">Mobile Payment</option>
                    <option value="Online Payment">Online Payment</option>
                  </select>
                </div>

                <!-- 2. Method Specific Fields (vertically stacked) -->
                <!-- Cheque Details -->
                <template v-if="tempPayment.payment_method === 'Cheque'">
                  <div class="form-group mb-3">
                    <label>Cheque Issue Bank <span class="req">*</span></label>
                    <select v-model="tempPayment.cheque_issue_bank_id" @change="loadBankBranchList(tempPayment)"
                      class="form-control">
                      <option disabled value="">-- Select Issue Bank --</option>
                      <option v-for="bank in allbankList" :key="bank.id" :value="bank.id">
                        {{ bank.bank_name }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group mb-3">
                    <label>Cheque Issue Branch <span class="req">*</span></label>
                    <select v-model="tempPayment.cheque_issue_branch_id" class="form-control">
                      <option disabled value="">-- Select Issue Branch --</option>
                      <option v-for="branch in branchesList || []" :key="branch.id" :value="branch.id">
                        {{ branch.branch_name }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group mb-3">
                    <label>Cheque No <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.cheque_no" placeholder="Enter Cheque No"
                      class="form-control" />
                  </div>
                  <div class="form-group mb-3">
                    <label>Cheque Issue Date <span class="req">*</span></label>
                    <input type="date" v-model="tempPayment.cheque_date" class="form-control" />
                  </div>
                </template>

                <!-- Bank Transfer Details -->
                <template v-else-if="tempPayment.payment_method === 'Bank Transfer'">
                  <div class="form-group mb-3">
                    <label>Bank Account <span class="req">*</span></label>
                    <select v-model="tempPayment.bank_account" class="form-control">
                      <option disabled value="">-- Select Bank Account --</option>
                      <option v-for="bank in bankList" :key="bank.id" :value="bank.id">
                        {{ bank.bank_name }} -
                        {{ maskBankAccountNo(bank.account_no) }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group mb-3">
                    <label>Reference Code <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.trans_ref_code" placeholder="Enter Reference Code"
                      class="form-control" />
                  </div>
                </template>

                <!-- Advance Payment Details -->
                <template v-else-if="tempPayment.payment_method === 'Advance'">
                  <div class="form-group mb-3">
                    <label>Select Advance Payment <span class="req">*</span></label>
                    <select v-model="tempPayment.selectedAdvancePaymentId" class="form-control">
                      <option disabled value="">-- Select Advance Payment --</option>
                      <option v-for="adv in advancePaymentsList" :key="adv.advance_payment_id"
                        :value="adv.advance_payment_id" :disabled="getAdvanceRemaining(adv.advance_payment_id) <= 0">
                        {{ adv.pay_method }} - Rs.{{
                          formatNumber(getAdvanceRemaining(adv.advance_payment_id))
                        }}
                        ({{ formatDateTime(adv.paid_at) }})
                      </option>
                    </select>
                  </div>
                </template>

                <!-- Credit/Debit Card Details -->
                <template v-else-if="tempPayment.payment_method === 'Credit/Debit Card'">
                  <div class="form-group mb-3">
                    <label>Card Type <span class="req">*</span></label>
                    <select v-model="tempPayment.card_type" class="form-control">
                      <option disabled value="">-- Select Card Type --</option>
                      <option value="Visa">Visa</option>
                      <option value="MasterCard">MasterCard</option>
                      <option value="American Express">American Express</option>
                      <option value="Discover">Discover</option>
                      <option value="UnionPay">UnionPay</option>
                      <option value="JCB">JCB</option>
                      <option value="Diners Club">Diners Club</option>
                    </select>
                  </div>
                  <div class="form-group mb-3">
                    <label>Last 4 Digits <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.card_last_four" placeholder="e.g. 1234" maxlength="4"
                      @input="tempPayment.card_last_four = tempPayment.card_last_four.replace(/\D/g, '')"
                      class="form-control" />
                  </div>
                </template>

                <!-- Mobile Payment Details -->
                <template v-else-if="tempPayment.payment_method === 'Mobile Payment'">
                  <div class="form-group mb-3">
                    <label>Mobile Payment Type <span class="req">*</span></label>
                    <select v-model="tempPayment.mobile_payment_type" class="form-control">
                      <option disabled value="">-- Select Mobile Payment Type --</option>
                      <option value="QR">QR</option>
                      <option value="Wallet">Wallet</option>
                      <option value="Bank Mobile Apps">Bank Mobile Apps</option>
                    </select>
                  </div>
                  <div class="form-group mb-3">
                    <label>Provider <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.provider" placeholder="Enter Provider"
                      class="form-control" />
                  </div>
                  <div class="form-group mb-3">
                    <label>Reference No <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.trans_ref_code" placeholder="Enter Reference No"
                      class="form-control" />
                  </div>
                </template>

                <!-- Online Payment Details -->
                <template v-else-if="tempPayment.payment_method === 'Online Payment'">
                  <div class="form-group mb-3">
                    <label>Payment Gateway <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.payment_gateway" placeholder="Enter Payment Gateway"
                      class="form-control" />
                  </div>
                  <div class="form-group mb-3">
                    <label>Reference No <span class="req">*</span></label>
                    <input type="text" v-model="tempPayment.trans_ref_code" placeholder="Enter Reference No"
                      class="form-control" />
                  </div>
                </template>

                <!-- 3. Payment Amount Field (LAST ONE) -->
                <div class="form-group mb-3">
                  <label>Payment Amount (Rs) <span class="req">*</span></label>
                  <div class="amount-input-container" style="position: relative">
                    <input type="number" v-model.number="tempPayment.amount" :placeholder="formatNumber(dueAmount)"
                      step="0.01" min="0" @keydown.enter="addInvoicePayment(tempPayment)" class="form-control pr-5" />

                    <!-- Inline button -->
                    <button type="button" class="btn btn-sm btn-fill-due" @click="fillDueAmount" style="
                        position: absolute;
                        right: 5px;
                        top: 50%;
                        transform: translateY(-50%);
                      ">
                      Fill Due Amount
                    </button>
                  </div>
                </div>

                <!-- 4. Add Payment Button -->
                <div class="d-flex justify-content-end mt-3">
                  <button class="btn btn-add" @click="addInvoicePayment(tempPayment)">
                    <i class="fa-solid fa-plus"></i> Add Payment
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Right side - Added Payments List -->
          <div class="col-md-6">
            <div class="card payment-list-card p-3 h-100">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h5 class="mb-0 font-weight-bold">Payments ({{ invoicePayments.length }})</h5>
                <button v-if="invoicePayments.length > 0" class="btn btn-sm btn-danger delete-all-btn"
                  @click="showDeleteAllConfirmation" title="Remove All Payments">
                  <i class="fa-solid fa-trash"></i> Remove All
                </button>
              </div>

              <!-- List of Added Payments -->
              <div v-if="invoicePayments.length > 0" class="added-payments-vertical-list">
                <div v-for="(payment, index) in invoicePayments" :key="index" class="payment-card-vertical mb-2">
                  <div class="payment-card-content">
                    <div class="payment-icon">
                      <i :class="getPaymentMethodIcon(payment.payment_method)"></i>
                    </div>
                    <div class="payment-detail-card">
                      <div class="payment-method-title">
                        {{ payment.payment_method }}
                      </div>
                      <div v-if="getPaymentReference(payment)" class="payment-reference-subtext">
                        {{ getPaymentReference(payment) }}
                      </div>
                      <div class="payment-amount-date">
                        <span class="payment-amount">Rs. {{ formatNumber(payment.amount) }}</span>
                        <span class="payment-date">{{
                          formatDateTime(payment.date)
                        }}</span>
                      </div>
                    </div>
                    <div class="payment-actions">
                      <button class="btn btn-sm btn-danger btn-remove"
                        @click="showDeleteSingleConfirmation(payment.index)" title="Remove Payment">
                        <i class="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5 text-muted bg-light rounded border border-dashed">
                <i class="fa-solid fa-receipt fa-2x mb-2"></i>
                <p class="mb-1 font-weight-bold">No payments added yet</p>
                <small class="text-muted">Fill the form on the left to add payments.</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice Category Card (Customer Only) -->
      <div v-if="invoiceType === 'customer'" class="col-sm-12 invoice-category-wrapper">
        <div class="card invoice-category-card p-3">
          <h6 class="mb-2 font-weight-bold" style="font-size: 14px;">Invoice Type</h6>

          <div class="type-box-selector" style="max-width: 320px;">
            <label class="type-box" :class="{ active: vatType === 'vat' }">
              <input type="radio" value="vat" v-model="vatType" class="d-none" />
              <span>VAT</span>
            </label>
            <label class="type-box" :class="{ active: vatType === 'non_vat' }">
              <input type="radio" value="non_vat" v-model="vatType" class="d-none" />
              <span>Non-VAT</span>
            </label>
          </div>

          <!-- Customer Details Box inside Invoice Category Card -->
          <div v-if="customerData.name && !showCustomerModal"
            class="customer-details-box mt-3 p-3 border rounded bg-light"
            style="color: #000; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);">
            <h6 class="mb-3 font-weight-bold">Customer Details</h6>
            <p class="mb-1"><strong>Name:</strong> {{ customerData.name }}</p>
            <p class="mb-1">
              <strong>Address:</strong> {{ customerData.address }}
            </p>
            <p class="mb-1">
              <strong>Mobile:</strong> {{ customerData.mobile }}
            </p>
            <p v-if="vatType === 'vat'" class="mb-1">
              <strong>VAT No:</strong> {{ customerData.vat_no }}
            </p>
            <div class="mt-3 text-right">
              <button class="btn btn-sm btn-primary" style="margin-right: 15px; padding: 6px 16px"
                @click="showCustomerModal = true">
                Edit
              </button>
              <button class="btn btn-sm btn-danger" style="padding: 6px 16px" @click="removeCustomerDetails">
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Save & Cancel Action Buttons -->
      <div
        class="col-sm-12 form-actions-wrapper d-flex justify-content-end align-items-center save-button-container gap-2">
        <button @click="resetForm" class="btn btn-danger">Cancel</button>

        <button @click="saveInvoicePayment" :disabled="saveButtonDisabled" class="btn btn-success save-btn ml-2">
          <span v-if="saveButtonDisabled" class="spinner-border spinner-border-sm"></span>
          <span v-else>Save Invoice</span>
        </button>
      </div>
    </template>

    <!-- Edit Modal -->
    <edit-modal v-if="showEditModal" :show="showEditModal" :itemName="editItemName" :itemQuantity="editItemQuantity"
      :items="items" :availableStock="editAvailableStock" @close="closeEditModal" @update="updateItem"></edit-modal>

    <!-- Delete Items Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content-confirmation">
        <span class="close" @click="closeDeleteModal">&times;</span>
        <p>Are you sure you want to delete this item?</p>
        <div class="button-container">
          <button type="button" class="btn btn-danger" @click="deleteItem">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Payments Confirmation Modal -->
  <div v-if="showConfirmationModal" class="modal-overlay confirmation-overlay">
    <div class="confirmation-modal">
      <div class="confirmation-header">
        <h4>{{ confirmationTitle }}</h4>
      </div>
      <div class="confirmation-body">
        <p>{{ confirmationMessage }}</p>
      </div>
      <div class="confirmation-footer">
        <button class="btn btn-danger" @click="closeConfirmation">No</button>
        <button class="btn btn-success" @click="confirmAction">Yes</button>
      </div>
    </div>
  </div>

  <div v-if="showCustomerModal" class="modal-overlay confirmation-overlay">
    <div class="confirmation-modal">
      <div class="confirmation-header">
        <h4>Customer Details</h4>
      </div>

      <div class="confirmation-body">
        <div class="form-group">
          <label>Name <span class="req">*</span></label>
          <input type="text" v-model="customerData.name" placeholder="Enter customer name" class="form-control" />
        </div>

        <div class="form-group">
          <label>Address <span v-if="vatType === 'vat'" class="req">*</span></label>
          <input type="text" v-model="customerData.address" placeholder="Enter address" class="form-control" />
        </div>

        <div class="form-group">
          <label>Mobile <span v-if="vatType === 'vat'" class="req">*</span></label>
          <input type="text" v-model="customerData.mobile" placeholder="Enter mobile number" class="form-control" />
        </div>

        <div class="form-group" v-if="vatType === 'vat'">
          <label>VAT No <span class="req">*</span></label>
          <input type="text" v-model="customerData.vat_no" placeholder="Enter VAT number" class="form-control" />
        </div>

        <div class="modal-footer">
          <button @click="confirmCustomerData" class="btn btn-success">
            Save
          </button>
          <button @click="closeCustomerModal" class="btn btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <InvoiceModal :showModal="showModal" :invoiceNo="selectedInvoiceNo" @close="closeModal" />
</template>

<script>
import {
  fetchDistributors,
  fetchDealerListManagement,
  //fetchItemPricesAndStock,
  getBankList,
  getBanksByLoginUser,
  getBranchesByBankId,
  saveCustomerInvoice,
  getDealerFinancials,
  fetchItemPricesAndStockByDealer,
} from "@/services/dataService";
import EditModal from "@/pages/Modal/editModal.vue";
import { useToast } from "vue-toastification";
import InvoiceModal from "@/pages/Modal/invoicePopup.vue";

export default {
  components: {
    EditModal,
    InvoiceModal,
  },
  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    const toast = useToast();
    return {
      localToken: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      userCategory: localStorage.getItem("user_label") || "",
      companyId: localStorage.getItem("company_id"),
      distributorVatCode: localStorage.getItem("distributor_vat_code"),
      currentDate,

      invoiceType: "customer",
      customerDealer: "",
      dealerList: [],
      distributorList: [],
      items: [],
      selectedItems: [],
      selectedVariantId: "",
      itemQuantity: 0,
      selectedStock: 0,
      // discount: 0,

      selectedDistributor: "",
      selectedDistrictId: null,
      isDistributorLocked: false,
      selectedDealerId: null,
      filters: {
        distributor: "",
      },

      bankList: [],
      allbankList: [],
      branchesList: [],
      tempPayment: {
        payment_method: "",
        amount: "",
        cheque_no: "",
        cheque_date: "",
        cheque_issue_bank_id: "",
        cheque_issue_branch_id: "",
        bank_account: "",
        trans_ref_code: "",
        selectedAdvancePaymentId: "",
        card_type: "",
        card_last_four: "",
        mobile_payment_type: "",
        provider: "",
        payment_gateway: "",
      },
      invoicePayments: [],
      // discount: 0,
      // discountType: 'value',

      // Dealer financial data
      dealerFinancials: null,
      dealerFinancialsLoading: false,
      showDuePayments: false,
      showAdvancePayments: false,

      isEditing: false,
      editIndex: -1,
      loading: false,
      showEditModal: false,
      editItemName: "",
      editItemQuantity: 0,
      editAvailableStock: null,
      showDeleteModal: false,
      deleteIndex: -1,
      toast,
      saveButtonDisabled: false,

      showConfirmationModal: false,
      confirmationTitle: "",
      confirmationMessage: "",
      pendingAction: null,
      pendingActionParams: null,

      scrollState: {
        isAtStart: true,
        isAtEnd: false,
      },

      vatType: "",
      showCustomerModal: false,
      customerData: {
        name: "",
        address: "",
        mobile: "",
        vat_no: "",
      },
      showModal: false,
      selectedInvoiceNo: null,
      showPrintButton: false,
      lastSavedInvoice: null,
    };
  },
  computed: {
    grandTotal() {
      return this.selectedItems.reduce((sum, item) => sum + item.vatTotal, 0);
    },
    netTotal() {
      return this.selectedItems.reduce((sum, item) => sum + item.total, 0);
    },
    finalVatTotal() {
      return this.selectedItems.reduce((sum, item) => sum + item.vat, 0);
    },
    // AFTER
    finalTotal() {
      // DISCOUNT LOGIC — commented out
      // let discountAmount = 0;
      // if (this.discountType === 'percentage') {
      //   discountAmount = (this.grandTotal * this.discount) / 100;
      // } else {
      //   discountAmount = this.discount;
      // }
      // return Math.max(this.grandTotal - discountAmount, 0);

      return this.grandTotal; // no discount applied
    },
    addedTotal() {
      return this.invoicePayments.reduce((sum, p) => sum + Number(p.amount), 0);
    },
    dueAmount() {
      const total = Number(this.finalTotal) || 0;
      const paid = Number(this.addedTotal) || 0;

      // Calculate the due in whole cents to avoid
      // displaying floating-point precision errors.
      const totalCents = Math.round(total * 100);
      const paidCents = Math.round(paid * 100);

      return Math.max(totalCents - paidCents, 0) / 100;
    },
    isAtStart() {
      return this.scrollState.isAtStart;
    },
    isAtEnd() {
      return this.scrollState.isAtEnd;
    },
    effectiveVatType() {
      if (this.invoiceType === "dealer") {
        const dealer = this.dealerList.find(
          (d) => (d.profile_id ?? d.id) == this.selectedDealerId
        );
        return dealer?.vat_liable == 1 ? "vat" : "non_vat";
      }
      return this.vatType;
    },
    previousDue() {
      return this.dealerFinancials?.total_due_amount ?? 0;
    },
    creditLimit() {
      return this.dealerFinancials?.credit_limit ?? 0;
    },
    totalDueWithInvoice() {
      const previousCents = Math.round(
        (Number(this.previousDue) || 0) * 100
      );

      const currentCents = Math.round(
        (Number(this.dueAmount) || 0) * 100
      );

      return (previousCents + currentCents) / 100;
    },
    availableCredit() {
      return Math.max(this.creditLimit - this.totalDueWithInvoice, 0);
    },
    creditExceeded() {
      return (
        this.creditLimit > 0 && this.totalDueWithInvoice > this.creditLimit
      );
    },
    exceedingAmount() {
      return Math.abs(this.creditLimit - this.totalDueWithInvoice);
    },
    duePaymentsList() {
      return this.dealerFinancials?.due_payments ?? [];
    },
    advancePaymentsList() {
      return this.dealerFinancials?.advance_payments ?? [];
    },
    cashSaleDealerList() {
      return this.dealerList.filter((d) => this.isCashSaleDealer(d));
    },
    regularDealerList() {
      return this.dealerList.filter((d) => !this.isCashSaleDealer(d));
    },
  },

  watch: {
    selectedVariantId(newVal) {
      this.selectedStock = this.getAvailableStock(newVal);
    },
    invoiceType(newVal, oldVal) {
      if (oldVal !== null && newVal !== oldVal) {
        this.partialReset();
        this.items = [];

        if (newVal === "dealer") {
          // Switching to dealer — clear so user must pick a dealer
          this.selectedDealerId = null;
          this.customerDealer = "";
        } else if (newVal === "customer") {
          const cashSale = this.cashSaleDealerList[0];
          this.customerDealer = cashSale ? this.getDealerLabel(cashSale) : "";
          this.selectedDealerId = cashSale ? (cashSale.profile_id ?? cashSale.id) : null;

          if (!cashSale && this.dealerList.length > 0) {
            this.toast.error("No CASH SALE dealer found for this distributor.");
          }
        }
      }
    },
    vatType(newVal) {
      if (this.invoiceType !== "customer") return;
      if (newVal && !this.customerData.name) {
        this.showCustomerModal = true;
      } else if (newVal === "vat" && !this.customerData.vat_no) {
        this.showCustomerModal = true;
      }
    },
    selectedDistributor(newVal) {
      this.filters.distributor = newVal;

      if (newVal) {
        this.onDistributorChange(newVal);
      } else {
        this.customerDealer = "";
      }
    },
    showModal(newVal) {
      document.body.style.overflow = newVal ? "hidden" : "";
    },
    // AFTER
    selectedDealerId(newVal, oldVal) {
      // Only reset if user changed dealer (not on initial programmatic set)
      if (oldVal !== null && newVal !== oldVal) {
        this.partialReset();
      }

      if (newVal) {
        this.loadItemPricesAndStockByDealer(newVal);
      }

      if (this.invoiceType === "dealer" && newVal) {
        console.log("Selected Dealer Profile ID:", newVal);
        console.log("Logged User Profile ID:", this.profileId);
        this.loadDealerFinancials(newVal);
      }
    },
    'tempPayment.payment_method'(newVal) {
      // Clear fields of other payment methods to avoid pollution
      this.tempPayment.cheque_no = "";
      this.tempPayment.cheque_date = "";
      this.tempPayment.cheque_issue_bank_id = "";
      this.tempPayment.cheque_issue_branch_id = "";
      this.tempPayment.bank_account = "";
      this.tempPayment.trans_ref_code = "";
      this.tempPayment.selectedAdvancePaymentId = "";
      this.tempPayment.card_type = "";
      this.tempPayment.card_last_four = "";
      this.tempPayment.mobile_payment_type = "";
      this.tempPayment.provider = "";
      this.tempPayment.payment_gateway = "";
      this.branchesList = [];
    },
  },

  mounted() {
    this.loadDistributors();
    this.loadBankList();
    this.loadAllBankList();
  },

  methods: {
    getDealerLabel(dealer) {
      return (
        dealer.business_name ||
        dealer.business_display_name ||
        dealer.name ||
        ""
      );
    },

    isCashSaleDealer(dealer) {
      return this.getDealerLabel(dealer).toUpperCase().includes("CASH SALE");
    },

    async addItem() {
      try {
        if (!this.filters.distributor) {
          this.toast.error("Please select a distributor");
          return;
        }
        if (!this.selectedVariantId) {
          this.toast.error("Please select an item");
          return;
        }
        if (this.itemQuantity <= 0) {
          this.toast.error("Please enter a valid quantity");
          return;
        }

        this.loading = true;

        const existingItem = this.selectedItems.find(
          (item) => item.itemVariantId === this.selectedVariantId
        );

        if (existingItem) {
          this.toast.error("Same item already exists in the list");
        } else {
          const itemData = this.items.find(
            (i) => i.id == this.selectedVariantId
          );
          if (!itemData) return;

          const availableStock = this.getAvailableStock(this.selectedVariantId);
          if (this.itemQuantity > availableStock) {
            this.toast.error("Quantity exceeds available stock");
            return;
          }

          const dealerBalance = itemData.dealer_balance_qty;
          if (dealerBalance !== null && dealerBalance !== undefined && this.itemQuantity > dealerBalance) {
            this.toast.error(
              `Exceeds the dealer's maximum stock limit. Remaining quantity: ${dealerBalance}`
            );
            return;
          }

          const price = itemData.consumer_price;
          const vat = itemData.consumer_vat_amount;
          const priceWithVat = itemData.consumer_price_with_vat;

          this.selectedItems.push({
            itemVariantId: this.selectedVariantId,
            quantity: this.itemQuantity,
            price: price,
            unitVat: vat,
            vat: vat * this.itemQuantity,
            priceWithVat: priceWithVat,
            total: price * this.itemQuantity,
            vatTotal: priceWithVat * this.itemQuantity,
          });
        }

        this.selectedVariantId = "";
        this.itemQuantity = 0;
      } catch (error) {
        console.error("Error adding item:", error);
      } finally {
        this.loading = false;
      }
    },

    editItem(index) {
      const itemVariantId = String(this.selectedItems[index].itemVariantId);
      this.editItemName = itemVariantId;
      this.editItemQuantity = this.selectedItems[index].quantity;
      // Get the original available stock (before this item's quantity was added)
      this.editAvailableStock = this.getAvailableStock(itemVariantId) + this.selectedItems[index].quantity;
      this.isEditing = true;
      this.editIndex = index;
      this.showEditModal = true;
    },

    updateItem(updatedItem) {
      if (updatedItem.itemQuantity > 0) {
        if (updatedItem.itemQuantity > this.editAvailableStock) {
          this.toast.error("Quantity exceeds available stock");
          return;
        }

        const editedVariant = this.items.find(
          (i) => i.id == this.selectedItems[this.editIndex].itemVariantId
        );
        const editBalance = editedVariant?.dealer_balance_qty;
        if (editBalance !== null && editBalance !== undefined && updatedItem.itemQuantity > editBalance) {
          this.toast.error(
            `Exceeds the dealer's maximum stock limit. Remaining quantity: ${editBalance}`
          );
          return;
        }

        this.selectedItems[this.editIndex].quantity = updatedItem.itemQuantity;
        this.selectedItems[this.editIndex].vat =
          this.selectedItems[this.editIndex].unitVat * updatedItem.itemQuantity;
        this.selectedItems[this.editIndex].total =
          this.selectedItems[this.editIndex].price * updatedItem.itemQuantity;
        this.selectedItems[this.editIndex].vatTotal =
          this.selectedItems[this.editIndex].priceWithVat *
          updatedItem.itemQuantity;
        this.closeEditModal();
      } else {
        this.toast.error("Please enter a valid quantity");
      }
    },

    closeEditModal() {
      this.showEditModal = false;
      this.isEditing = false;
      this.editIndex = -1;
      this.editItemName = "";
      this.editItemQuantity = 0;
      this.editAvailableStock = null;
    },

    closeInvoicePaymentModal() {
      this.showInvoicePaymentModal = false;
    },

    removeItem(index) {
      this.confirmDeleteItem(index);
    },

    confirmDeleteItem(index) {
      this.deleteIndex = index;
      this.showDeleteModal = true;
    },

    deleteItem() {
      if (this.deleteIndex > -1) {
        const removedItem = this.selectedItems[this.deleteIndex];
        const item = this.items.find((i) => i.id == removedItem.itemVariantId);
        this.selectedItems.splice(this.deleteIndex, 1);
        this.closeDeleteModal();
      }
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteIndex = -1;
    },

    async loadDistributors() {
      try {
        const res = await fetchDistributors(this.profileId, this.localToken);
        this.distributorList = res.data.data;

        if (this.userCategory === 'DISTRIBUTOR' || this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          this.isDistributorLocked = true;
          if (this.distributorList && this.distributorList.length > 0) {
            const dist = this.distributorList[0];
            this.selectedDistributor = String(dist.profile_id ?? dist.id);
          }
        } else {
          this.isDistributorLocked = false;
        }
      } catch (err) {
        console.error("Failed to load distributors:", err);
        this.toast.error("Failed to load distributor list.");
      }
    },

    async onDistributorChange(distributorId) {
      if (!distributorId) return;

      const selectedDist = this.distributorList.find(
        (d) => String(d.profile_id ?? d.id) === String(distributorId)
      );
      this.selectedDistrictId = selectedDist?.district_id ?? null;

      try {
        const response = await fetchDealerListManagement(
          this.profileId,
          distributorId,
          this.localToken
        );
        this.dealerList = response.data.data || [];

        if (this.invoiceType === "customer") {
          const cashSale = this.cashSaleDealerList[0];
          this.customerDealer = cashSale ? this.getDealerLabel(cashSale) : "";
          this.selectedDealerId = cashSale ? (cashSale.profile_id ?? cashSale.id) : null;

          if (!cashSale && this.dealerList.length > 0) {
            this.toast.error("No CASH SALE dealer found for this distributor.");
          }
        } else {
          this.customerDealer = "";
          this.selectedDealerId = null;
        }
      } catch (error) {
        console.error("Error loading dealer_visit list:", error);
        this.dealerList = [];
      }

      // ❌ OLD METHOD — commented out, now using dealer-specific price API
      // await this.loadItemPricesAndStock();
    },

    async loadDealerFinancials(dealerId) {
      if (!dealerId) {
        this.dealerFinancials = null;
        return;
      }
      this.dealerFinancialsLoading = true;
      try {
        const payload = {
          profile_id: Number(this.profileId),
          dealer_id: Number(this.selectedDealerId),
          to_date: this.currentDate,
        };

        const response = await getDealerFinancials(payload, this.localToken);
        this.dealerFinancials = response.data;
        console.log("dealerFinancials", this.dealerFinancials);
      } catch (error) {
        console.error("Error loading dealer financials:", error);
        this.dealerFinancials = null;
      } finally {
        this.dealerFinancialsLoading = false;
      }
    },

    // ❌ OLD METHOD — commented out
    // async loadItemPricesAndStock() {
    //   try {
    //     const response = await fetchItemPricesAndStock(
    //       this.companyId,
    //       this.selectedDistrictId,
    //       this.profileId,
    //       this.localToken
    //     );
    //     if (response.data.status === "success") {
    //       this.items = response.data.data;
    //     }
    //   } catch (error) {
    //     console.error("Failed to load prices", error);
    //   }
    // },


    async loadItemPricesAndStockByDealer(dealerId) {
      this.items = [];

      if (!dealerId) {
        return;
      }

      try {
        const response = await fetchItemPricesAndStockByDealer(
          dealerId,
          this.profileId,
          this.localToken
        );

        // Ignore a response belonging to a previous selection.
        if (String(this.selectedDealerId) !== String(dealerId)) {
          return;
        }

        if (
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          this.items = response.data.data;
        } else {
          this.items = [];
          this.toast.error("Unable to load items for the selected dealer.");
        }
      } catch (error) {
        if (String(this.selectedDealerId) !== String(dealerId)) {
          return;
        }

        this.items = [];

        console.error("Failed to load items by dealer:", error);

        this.toast.error(
          "Unable to load item prices and stock. Please try again."
        );
      }
    },

    getAvailableStock(variantId) {
      const variant = this.items.find((i) => i.id == variantId);
      if (!variant) return 0;

      const itemId = variant.item_id;
      const totalStock = variant.current_stock || 0;

      const usedQty = this.selectedItems
        .filter((i) => {
          const v = this.items.find((x) => x.id == i.itemVariantId);
          return v && v.item_id == itemId;
        })
        .reduce((sum, i) => sum + i.quantity, 0);

      return totalStock - usedQty;
    },

    getItemNameById(variantId) {
      const item = this.items.find((i) => i.id == variantId);
      return item ? item.variant_name : "";
    },

    isItemSelected(variantId) {
      return this.selectedItems.some((item) => item.itemVariantId == variantId);
    },

    resetTableData() {
      this.selectedItems = [];
      this.availableItems = [...this.items];
    },

    formatNumber(value) {
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // Formatting date and time
    formatDateTime(datetime) {
      if (!datetime) return "";
      const date = new Date(datetime);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    // Load bank list for bank transfer payments
    async loadBankList() {
      try {
        const response = await getBanksByLoginUser(
          this.profileId,
          this.localToken
        );
        this.bankList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching Banks:", error);
      }
    },

    // Load all banks for cheque payments
    async loadAllBankList() {
      try {
        const response = await getBankList(this.localToken);
        this.allbankList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching all banks:", error);
      }
    },

    // Load branches based on selected bank for cheque payments
    async loadBankBranchList(tempPayment) {
      try {
        if (!tempPayment.cheque_issue_bank_id) return;
        const payload = { bank_id: tempPayment.cheque_issue_bank_id };
        const response = await getBranchesByBankId(payload, this.localToken);
        this.branchesList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching Branches:", error);
      }
    },

    // Masking bank account numbers
    maskBankAccountNo(accountNo) {
      if (!accountNo) return "";
      const account = accountNo.toString();
      if (account.length <= 4) return account;

      const lastFour = account.slice(-4);
      const masked = "*".repeat(account.length - 4);
      return masked + lastFour;
    },

    fillDueAmount() {
      const due = Number(this.dueAmount);

      if (!Number.isFinite(due) || due <= 0) {
        this.toast.error("No remaining amount to fill");
        return;
      }

      // Keep the input value numeric and rounded to cents.
      // Do not assign a formatted string containing commas.
      this.tempPayment.amount = Number(due.toFixed(2));
    },

    getAdvanceRemaining(advanceId) {
      const adv = this.advancePaymentsList.find(
        (a) => a.advance_payment_id === advanceId
      );
      console.log("adv", adv);
      if (!adv) return 0;
      const used = this.invoicePayments
        .filter((p) => p.advance_payment_id === advanceId)
        .reduce((sum, p) => sum + Number(p.amount), 0);
      return adv.advance_balance - used;
    },

    // Add payment to the invoicePayments array
    addInvoicePayment(tempPayment) {
      if (!tempPayment.payment_method || !tempPayment.amount) {
        this.toast.error("Please select payment method and enter amount.");
        return;
      }

      if (this.dueAmount <= 0) {
        this.toast.warning("No due amount remaining to add another payment.");
        return;
      }

      if (tempPayment.amount <= 0) {
        this.toast.error("Enter valid amount.");
        return;
      }

      if (Number(tempPayment.amount) > this.dueAmount) {
        this.toast.error("Payment amount exceeds current due amount!");
        return;
      }

      if (tempPayment.payment_method === "Advance") {
        if (!tempPayment.selectedAdvancePaymentId) {
          this.toast.error("Please select an advance payment.");
          return;
        }
        const advanceRemaining = this.getAdvanceRemaining(
          tempPayment.selectedAdvancePaymentId
        );
        if (Number(tempPayment.amount) > advanceRemaining) {
          this.toast.error(
            `Payment amount exceeds the selected advance payment balance`
          );
          return;
        }
      }

      if (tempPayment.payment_method === "Credit/Debit Card") {
        if (!tempPayment.card_type) {
          this.toast.error("Please select a card type.");
          return;
        }
        if (!tempPayment.card_last_four) {
          this.toast.error("Please enter the last 4 digits of the card.");
          return;
        }
        if (!/^\d{4}$/.test(tempPayment.card_last_four)) {
          this.toast.error("Last 4 digits must be exactly 4 numeric digits.");
          return;
        }
      }

      if (tempPayment.payment_method === "Mobile Payment") {
        if (!tempPayment.mobile_payment_type) {
          this.toast.error("Please select a mobile payment type.");
          return;
        }
        if (!tempPayment.provider) {
          this.toast.error("Please enter a provider.");
          return;
        }
        if (!tempPayment.trans_ref_code) {
          this.toast.error("Please enter a reference number.");
          return;
        }
      }

      if (tempPayment.payment_method === "Online Payment") {
        if (!tempPayment.payment_gateway) {
          this.toast.error("Please enter a payment gateway.");
          return;
        }
        if (!tempPayment.trans_ref_code) {
          this.toast.error("Please enter a reference number.");
          return;
        }
      }

      const paymentIndex = this.invoicePayments.length;
      console.log("payment index", paymentIndex);
      console.log("tempPayment payment_method", tempPayment.payment_method);

      this.invoicePayments.push({
        // invoice_no: invoice.invoices_no,
        date: new Date().toISOString(),
        payment_method: tempPayment.payment_method,
        cheque_issue_bank_id: tempPayment.cheque_issue_bank_id || null,
        cheque_issue_branch_id: tempPayment.cheque_issue_branch_id || null,
        cheque_no: tempPayment.cheque_no || null,
        cheque_date: tempPayment.cheque_date || null,
        bank_account: tempPayment.bank_account || null,
        trans_ref_code: tempPayment.trans_ref_code || null,
        reference_no: tempPayment.trans_ref_code || null,
        advance_payment_id: tempPayment.selectedAdvancePaymentId || null,
        amount: Number(tempPayment.amount),
        status: 1,
        index: paymentIndex,
        card_type: tempPayment.card_type || null,
        card_last4_digits: tempPayment.card_last_four || null,
        mobile_payment_type: tempPayment.mobile_payment_type || null,
        mobile_provider: tempPayment.provider || null,
        online_gateway: tempPayment.payment_gateway || null,
      });

      // Reset form fields
      tempPayment.payment_method = "";
      tempPayment.amount = "";
      tempPayment.cheque_issue_bank_id = "";
      tempPayment.cheque_issue_branch_id = "";
      tempPayment.cheque_no = "";
      tempPayment.cheque_date = "";
      tempPayment.bank_account = "";
      tempPayment.trans_ref_code = "";
      tempPayment.selectedAdvancePaymentId = "";
      tempPayment.card_type = "";
      tempPayment.card_last_four = "";
      tempPayment.mobile_payment_type = "";
      tempPayment.provider = "";
      tempPayment.payment_gateway = "";
      this.branchesList = [];
    },

    // Get icon class based on payment method
    getPaymentMethodIcon(method) {
      switch (method) {
        case "Cash":
          return "fa-solid fa-sack-dollar";
        case "Cheque":
          return "fa-solid fa-money-check";
        case "Card":
        case "Credit/Debit Card":
          return "fa-solid fa-credit-card";
        case "Bank Transfer":
          return "fa-solid fa-money-bill-transfer";
        case "Advance":
          return "fa-solid fa-money-bills";
        case "Mobile Payment":
          return "fa-solid fa-mobile-screen-button";
        case "Online Payment":
          return "fa-solid fa-globe";
        default:
          return "fa-solid fa-receipt";
      }
    },

    // Get payment reference based on method
    getPaymentReference(payment) {
      switch (payment.payment_method) {
        case "Cheque":
          return payment.cheque_no ? `Cheque No: ${payment.cheque_no}` : null;
        case "Bank Transfer":
        case "Bank transfer":
          return payment.trans_ref_code ? `Ref: ${payment.trans_ref_code}` : null;
        case "Advance":
          return payment.advance_ref ? `Ref: ${payment.advance_ref}` : null;
        case "Credit/Debit Card": {
          const cardType = payment.card_type || "Card";
          const last4 = payment.card_last4_digits || payment.card_last_four || "";
          return last4 ? `${cardType} - ************${last4}` : cardType;
        }
        case "Mobile Payment": {
          const rawType = payment.mobile_payment_type || "";
          const type = rawType.toUpperCase() === "QR" ? "QR" :
            rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : "";
          const provider = payment.mobile_provider || payment.provider || "";
          const ref = payment.trans_ref_code || payment.reference_no || "";
          const parts = [];
          if (type) parts.push(type);
          if (provider) parts.push(provider);
          if (ref) parts.push(`Ref: ${ref}`);
          return parts.length > 0 ? parts.join(" - ") : null;
        }
        case "Online Payment": {
          const gateway = payment.online_gateway || payment.payment_gateway || "";
          const ref = payment.trans_ref_code || payment.reference_no || "";
          const parts = [];
          if (gateway) parts.push(gateway);
          if (ref) parts.push(`Ref: ${ref}`);
          return parts.length > 0 ? parts.join(" - ") : null;
        }
        default:
          return null;
      }
    },

    // Confirmation Modal Methods
    showDeleteConfirmation(title, message, action, params = null) {
      this.confirmationTitle = title;
      this.confirmationMessage = message;
      this.pendingAction = action;
      this.pendingActionParams = params;
      this.showConfirmationModal = true;
    },

    // Execute the confirmed action
    confirmAction() {
      if (this.pendingAction) {
        if (this.pendingActionParams) {
          this.pendingAction(...this.pendingActionParams);
        } else {
          this.pendingAction();
        }
      }
      this.closeConfirmation();
    },

    // Close confirmation modal and reset state
    closeConfirmation() {
      this.showConfirmationModal = false;
      this.pendingAction = null;
      this.pendingActionParams = null;
      this.confirmationTitle = "";
      this.confirmationMessage = "";
    },

    // Show confirmation for single payment deletion
    showDeleteSingleConfirmation(index) {
      this.showDeleteConfirmation(
        "Confirm Delete",
        "Do you want to delete this payment?",
        this.removeInvoicePayment,
        [index]
      );
    },

    // Show confirmation for all payments deletion
    showDeleteAllConfirmation() {
      const paymentCount = this.invoicePayments.length;

      if (paymentCount === 0) {
        this.toast.warning("No payments to remove for this invoice.");
        return;
      }

      this.showDeleteConfirmation(
        "Confirm Delete",
        `Do you want to delete all ${paymentCount} payment(s) for this invoice?`,
        this.removeAllInvoicePayments
      );
    },

    // Remove a single payment by index
    removeInvoicePayment(index) {
      this.invoicePayments.splice(index, 1);
      this.invoicePayments.forEach((payment, i) => {
        payment.index = i;
      });
      this.toast.success("Payment removed successfully!");
    },

    // Remove all payments for a specific invoice
    removeAllInvoicePayments() {
      this.invoicePayments = [];
      this.toast.success(`Removed all payments for this invoice`);
    },

    scrollPayments(direction) {
      const el = this.$refs.paymentList;
      if (!el) return;

      const scrollAmount = 280;
      el.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
      });
    },

    // Update scroll button states
    updateScrollButtons() {
      const el = this.$refs.paymentList;
      if (!el) return;

      this.scrollState.isAtStart = el.scrollLeft <= 0;
      this.scrollState.isAtEnd =
        el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    },

    confirmCustomerData() {
      if (!this.customerData.name) {
        this.toast.error("Please enter customer name.");
        return;
      }

      if (this.vatType === "vat") {
        if (!this.customerData.address) {
          this.toast.error("Please enter customer address.");
          return;
        }

        if (!this.customerData.mobile) {
          this.toast.error("Please enter customer mobile number.");
          return;
        }

        if (!this.customerData.vat_no) {
          this.toast.error("Please enter VAT number for VAT invoices.");
          return;
        }
      }

      if (this.customerData.mobile) {
        const mobileRegex = /^07\d{8}$/;
        if (!mobileRegex.test(this.customerData.mobile)) {
          this.toast.error("Enter a valid mobile number (e.g., 0771234567)");
          return;
        }
      }

      this.showCustomerModal = false;
    },

    closeCustomerModal() {
      this.showCustomerModal = false;
      if (!this.customerData.name) {
        this.customerData = { name: "", address: "", mobile: "", vat_no: "" };
        this.vatType = "";
      }
    },

    removeCustomerDetails() {
      this.customerData = { name: "", address: "", mobile: "", vat_no: "" };
      this.vatType = "";
    },

    // Save invoice payments to backend
    async saveInvoicePayment() {
      if (this.saveButtonDisabled) return;
      this.saveButtonDisabled = true;

      if (!this.filters.distributor) {
        this.toast.error("Please select distributor");
        this.saveButtonDisabled = false;
        return;
      }

      if (!this.selectedDealerId) {
        this.toast.error(
          this.invoiceType === "customer"
            ? "Please select a Customer Dealer"
            : "Please select a dealer"
        );

        this.saveButtonDisabled = false;
        return;
      }

      if (this.selectedItems.length === 0) {
        this.toast.error("Please add at least one item");
        this.saveButtonDisabled = false;
        return;
      }

      if (this.invoiceType === "dealer" && this.creditExceeded) {
        this.toast.error("Total due amount exceeds the dealer's credit limit.");
        this.saveButtonDisabled = false;
        return;
      }

      // if (this.invoiceType === 'customer' && this.dueAmount != 0) {
      //   this.toast.error("Please complete remaining due amount");
      //   this.saveButtonDisabled = false;
      //   return;
      // }

      if (this.invoiceType === "customer" && !this.customerData.name) {
        this.toast.error("Please fill customer details");
        this.saveButtonDisabled = false;
        return;
      }

      // if (this.invoiceType === 'dealer' && this.invoicePayments == '') {
      //   this.toast.error("Please add at least one payment");
      //   this.saveButtonDisabled = false;
      //   return;
      // }

      // Prepare Items Array
      const items = this.selectedItems.map((item) => ({
        variant_id: item.itemVariantId,
        item_qty: item.quantity,
        item_price:
          this.effectiveVatType === "vat" ? item.price : item.priceWithVat,
        item_vat: this.effectiveVatType === "vat" ? item.vat : 0,
        item_discount_value: 0,
        item_discount_percentage: 0,
        customer_discount_value: 0,
        customer_discount_percentage: 0,
        cost_price: 0,
        item_gross_total: item.vatTotal,
        item_net_total:
          this.effectiveVatType === "vat" ? item.total : item.vatTotal,
      }));

      // Prepare Payments Array
      const payments = this.invoicePayments.map((p) => ({
        payment_method: p.payment_method,
        cheque_issue_bank_id: p.cheque_issue_bank_id || null,
        cheque_issue_branch_id: p.cheque_issue_branch_id || null,
        cheque_no: p.cheque_no || null,
        cheque_date: p.cheque_date || null,
        bank_account: p.bank_account || null,
        trans_ref_code: p.trans_ref_code || null,
        amount: p.amount,
        reference_no: p.reference_no || null,
        status: p.status,
        advance_payment_id: p.advance_payment_id || null,
        transaction_id: p.transaction_id || null,
        amount_type: 0,
        settlment_receipt_no: null,
        card_type: p.card_type || null,
        card_last4_digits: p.card_last4_digits || null,
        mobile_payment_type: p.mobile_payment_type || null,
        mobile_provider: p.mobile_provider || null,
        online_gateway: p.online_gateway || null,
      }));

      console.log("grand", this.grandTotal);
      console.log("net", this.netTotal);
      console.log("vat", this.effectiveVatType);

      // Final Payload
      const payload = {
        invoice_date: this.currentDate,
        invoice_type: this.invoiceType,
        company_id: Number(this.companyId),
        distributor_id: Number(this.filters.distributor),
        dealer_id: Number(this.selectedDealerId),
        create_by:
          this.userCategory === "DISTRIBUTOR"
            ? Number(this.profileId)
            : this.userCategory === "DISTRIBUTOR BACK OFFICE"
              ? Number(this.reportingProfileId)
              : Number(this.profileId),
        distributor_vat_code: this.distributorVatCode,

        // AFTER
        discount_percentage: 0,
        discount_value: 0, // this.discount — commented out

        gross_total: this.grandTotal,
        net_total:
          this.effectiveVatType === "vat" ? this.netTotal : this.grandTotal,

        vat: this.finalVatTotal,
        nbt: 0,
        vat_liable: this.effectiveVatType === "vat" ? 1 : 0,
        vat_no: this.effectiveVatType === "vat" ? this.customerData.vat_no : 0,

        ...(this.invoiceType === "customer" && {
          customer_name: this.customerData.name,
          customer_address: this.customerData.address,
          customer_mobile: this.customerData.mobile,
        }),

        items,
        payments,
      };

      try {
        const response = await saveCustomerInvoice(payload, this.localToken);

        if (response.data.status === "success") {
          const selectedDealer = this.dealerList.find(
            (dealer) =>
              String(dealer.profile_id ?? dealer.id) ===
              String(this.selectedDealerId)
          );
          this.lastSavedInvoice = {
            invoiceNo: response.data.data?.invoices_no || "N/A",
            createdAt: this.currentDate,
            customerData: { ...this.customerData },
            selectedItems: [...this.selectedItems],
            invoicePayments: [...this.invoicePayments],
            grandTotal: this.grandTotal,
            netTotal: this.netTotal,
            finalVatTotal: this.finalVatTotal,
            discount: 0,
            finalTotal: this.finalTotal,
            vatType: this.effectiveVatType,
            distributorName: this.distributorList[0].name || "N/A",
            dealerName:
              selectedDealer?.business_name ||
              selectedDealer?.business_display_name ||
              selectedDealer?.name ||
              "N/A",
          };

          this.toast.success(
            response.data.message || "Invoice saved successfully."
          );
          this.resetForm();
          await this.showInvoiceDetails(this.lastSavedInvoice);
        }
      } catch (error) {
        console.error("Error:", error.response?.data || error);
        this.toast.error(
          error.response?.data?.message || "Failed to save invoice."
        );
      } finally {
        this.saveButtonDisabled = false;
      }
    },

    async showInvoiceDetails(invoice) {
      this.selectedInvoiceNo = invoice.invoiceNo;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },
    partialReset() {
      this.selectedItems = [];
      this.invoicePayments = [];
      this.items = [];
      // this.discount = 0;
      // this.discountType = 'value';
      this.customerData = { name: "", address: "", mobile: "", vat_no: "" };
      this.vatType = "";
      this.selectedVariantId = "";
      this.itemQuantity = 0;
      this.selectedStock = 0;
      this.dealerFinancials = null;
      this.showDuePayments = false;
      this.showAdvancePayments = false;
    },
    resetForm() {
      this.selectedItems = [];
      this.invoicePayments = [];
      // this.discount = 0;
      this.customerData = { name: "", address: "", mobile: "", vat_no: "" };
      this.vatType = "";
      this.selectedVariantId = "";
      this.itemQuantity = 0;
      if (!this.isDistributorLocked) {
        this.selectedDistributor = "";
        this.filters.distributor = "";
        this.customerDealer = "";
        this.selectedDealerId = null;
      }
      this.showPrintButton = false;
    },

    getDealerBalance(variantId) {
      const v = this.items.find((i) => i.id == variantId);
      return v?.dealer_balance_qty ?? null;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ==========================================================================
   1. GLOBAL FORM CONTAINER & BASE UI ENHANCEMENTS
   ========================================================================== */
.container {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06) !important;
  border-radius: 12px !important;
}

select,
input[type="number"],
.text-input,
input[type="date"],
input[type="text"] {
  border-radius: 8px !important;
  border: 1px solid #e5e7eb !important;
  background-color: #f9fafb !important;
  transition: all 0.2s ease !important;
}

select:focus,
input[type="number"]:focus,
.text-input:focus,
input[type="date"]:focus,
input[type="text"]:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
  background-color: #ffffff !important;
  outline: none !important;
}

.btn {
  border-radius: 8px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  font-weight: 500 !important;
  transition: all 0.2s !important;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn:active {
  transform: translateY(0);
}

/* ==========================================================================
   2. CARD WRAPPERS - UNIFORM 15PX VERTICAL CARD SPACING
   ========================================================================== */
/* Form Card Wrappers - Controls top margin spacing between each section card */
.header-card-wrapper,
.item-card-wrapper,
.dealer-financials-wrapper,
.payment-summary-wrapper,
.outstanding-due-wrapper,
.credit-limit-wrapper,
.payment-section-wrapper,
.invoice-category-wrapper,
.form-actions-wrapper {
  margin-top: 15px !important;
  margin-bottom: 0 !important;
}

.header-card-wrapper {
  margin-top: 0 !important;
}

/* ==========================================================================
   3. SEPARATE CARD STYLES BY PURPOSE
   ========================================================================== */

/* Top Header Info Card - Purpose: Top controls for date, distributor, type, and dealer */
.header-info-card {
  border: 1px solid #f3f4f6 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Item Selection Card - Purpose: Product selection, quantity input, and added items table */
.item-selection-card {
  border: 1px solid #f3f4f6 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Dealer Financial Overview Card - Purpose: Shows due payments and advance balances for dealer */
.dealer-financials-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Payment Summary Card - Purpose: Displays grand total, final total, and current due amounts */
.payment-summary-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Total Outstanding Due Card - Purpose: Shows previous invoices due and total due combined */
.outstanding-due-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Credit Limit Card - Purpose: Monitors credit limit, available credit, and warning status */
.credit-limit-card {
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
}

/* Payment Form Card - Purpose: Left form card for adding payment details */
.payment-form-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Payment List Card - Purpose: Right card displaying vertical list of added payment items */
.payment-list-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

/* Invoice Category Card - Purpose: Customer VAT/Non-VAT selector and customer details box */
.invoice-category-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background-color: #ffffff;
}

.dropdown-container label {
  color: #374151;
  font-weight: 600;
}

.table th {
  background-color: #f9fafb;
  color: #4b5563;
  font-weight: 600;
  border-bottom-width: 1px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.date-box {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  background-color: #f8f9fa;
  text-align: left !important;
}

.type-box-selector {
  display: flex;
  gap: 8px;
  align-items: center;
}

.type-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;
  color: #4b5563;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  margin-bottom: 0;
  height: 42px;
}

.type-box:hover:not(.disabled) {
  border-color: #2563eb;
  color: #1d4ed8;
  background-color: #eff6ff;
}

.type-box.active {
  border-color: #2563eb !important;
  background-color: #ebf5ff !important;
  color: #1e40af !important;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.12);
}

.type-box.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f3f4f6;
}

.dropdowns-wrapper {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  justify-content: center;
}


/* Full Width Item Adding Row */
.item-add-row {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  width: 100%;
}

.item-name-field {
  flex: 1;
  /* Expands to fill available space */
  min-width: 250px;
}

.item-qty-field {
  width: 180px;
  flex-shrink: 0;
}

.item-btn-field {
  width: 130px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.btn-add-item {
  height: 42px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  width: 100%;
  margin-left: 0 !important;
}

.dropdown-container {
  display: flex;
  flex: 1;
  min-width: 180px;
  max-width: 250px;
}

.dropdown-item {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

input[type="number"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

input[type="number"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.text-input {
  width: 100% !important;
  padding: 10px !important;
  border-radius: 5px !important;
  border: 1px solid #ccc !important;
  font-size: 14px !important;
  color: #656b72 !important;
}

.text-input:focus {
  box-shadow: none !important;
  outline: none !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
}

.fa {
  cursor: pointer;
  margin: 0 5px;
  font-size: 18px;
  color: #007bff;
  transition: color 0.3s;
}

.fa:hover {
  color: #0056b3;
}

.fa-print {
  margin: 0 0px !important;
  font-size: 15px !important;
  color: white !important;
}

.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.4);
}

.close {
  color: #aaa;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
}

.dropdown-container-modal {
  grid-column: 1 / 3;
}

button.btn-success {
  grid-column: 2 / 3;
  justify-self: end;
}

.button-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}

.modal-content-confirmation {
  background-color: #fff;
  padding: 30px;
  border: 1px solid #888;
  width: 500px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
}

.modal-content-confirmation .close {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #aaa;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
}

.modal-content-confirmation .close:hover,
.modal-content-confirmation .close:focus {
  color: black;
  text-decoration: none;
}

.modal-content-confirmation .button-container {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  grid-column: 1 / 3;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 5px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 13px;
  color: #333;
}

.discount-input {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-inline: 2;
}

/* Card Footer */
.card-footer {
  margin-top: -20px;
  padding-top: 15px;
  display: flex;
  justify-content: flex-end;
}

.btn-fill-due {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);

  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;

  background-color: #dfe3e7;
  border: 1px solid #dcdfe3;
  border-radius: 6px;

  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-fill-due:hover {
  background-color: #e2e6ea;
  border-color: #cfd4da;
}

.fill-btn:active {
  background-color: #d6d8db;
}

.btn-add {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 50px;
  background-color: #19183b;
  color: white;
}

.btn-add i {
  margin-right: 6px;
}

.btn-add:hover {
  background-color: #343078;
  color: white;
}

/* Added Payments Section */
.invoice-added-payments {
  border-top: 1px solid #e0e0e0;
}

.added-payments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.added-payments-header h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.delete-all-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.delete-all-btn:hover {
  background-color: #dc3545;
  border-color: #dc3545;
}

/* Horizontal Payments Container */
.horizontal-payments-container {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 40px;
}

.horizontal-payments-list {
  display: flex;
  gap: 12px;
  padding: 15px 0;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.horizontal-payments-list::-webkit-scrollbar {
  display: none;
}

/* Vertical Payments List & Layout */
.added-payments-vertical-list {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.payment-card-vertical {
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #e9ecef;
}

.payment-card-vertical:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.border-dashed {
  border-style: dashed !important;
}

/* Horizontal Payment Card */
.payment-card-horizontal {
  flex: 0 0 260px;
  /* Fixed width for all cards */
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #eee;
}

.payment-card-horizontal:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.payment-card-content {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
}

.payment-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.payment-icon i {
  font-size: 14px;
  color: #495057;
}

.payment-detail-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  /* Prevent text overflow */
}

.payment-method-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  line-height: 1.3;
}

.payment-reference-subtext {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  line-height: 1.3;
}

.payment-amount-date {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.payment-amount {
  font-weight: 600;
  font-size: 13px;
  color: #28a745;
}

.payment-date {
  font-size: 11px;
  color: #6c757d;
}

.payment-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.btn-remove {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 12px;
  border: none;
}

/* Modal Structure */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

/* Custom Confirmation Modal Styles */
.confirmation-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
}

.confirmation-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.confirmation-header {
  padding: 20px 24px 0;
  border-bottom: none;
}

.confirmation-header h4 {
  margin: 0;
  color: #dc3545;
  font-size: 1.25rem;
  font-weight: 600;
}

.confirmation-body {
  padding: 20px 24px;
}

.confirmation-body p {
  margin: 0;
  color: #495057;
  font-size: 14px;
  line-height: 1.5;
}

.confirmation-footer {
  padding: 16px 24px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.confirmation-footer .btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 500;
  min-width: 80px;
}

/* Scroll Buttons */
.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: white;
  border: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.scroll-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

.scroll-btn-prev {
  left: 10px;
}

.scroll-btn-next {
  right: 10px;
}

.scroll-btn i {
  font-size: 12px;
  color: #495057;
}

.vat-selector {
  display: flex;
  align-items: left;
  gap: 20px;
  margin-top: 10px;
}

/* Option wrapper */
.tick-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  padding-left: 28px;
  user-select: none;
}

/* Hide default radio */
.tick-option input[type="radio"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Custom circle */
.tick-circle {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 18px;
  width: 18px;
  border: 2px solid #555;
  border-radius: 50%;
  background-color: #fff;
  transition: all 0.2s ease;
}

/* Filled circle when checked */
.tick-option input[type="radio"]:checked+.tick-circle {
  background-color: #4caf50;
  border-color: #4caf50;
}

/* Optional inner dot */
.tick-option input[type="radio"]:checked+.tick-circle::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
}

.button-default {
  padding: 10px 20px;
  margin-left: 10px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.save-btn {
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.invoice-history {
  margin-top: 30px;
}

.invoice-history h3 {
  font-size: 18px;
  margin-bottom: 15px;
  color: #333;
}

.invoice-history-table {
  width: 100%;
  border-collapse: collapse;
}

.invoice-history-table th,
.invoice-history-table td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

.invoice-history-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.dealer-financials-card {
  border-left: 3px solid #4a90d9;
}

.financials-list {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.due-amount {
  display: flex;
  justify-content: space-between;
  margin-inline: 2;
  font-weight: bold;
  color: #dc3545;
}

.credit-card-success {
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background: #f5fff7;
  border: 1px solid #32cc59;
}

.credit-card-danger {
  border-radius: 10px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  background: #fff4f5;
  border: 1px solid #dc3545;
}
</style>
